'use client';

import { useCallback } from 'react';

import { parseAsStringLiteral, useQueryState } from 'nuqs';

const COACH_PARAM_VALUES = ['on'] as const;

export interface UseOpenStateReturn {
  isOpen: boolean;
  openCoach: () => void;
  closeCoach: () => void;
}

/**
 * Open/close state for the Deal Coach panel, backed by the
 * `?coach=on` search param. Reads and writes go through nuqs
 * with shallow history updates — no Next.js navigation, no
 * server round-trip — so a toggle commits on the next render.
 *
 * Every `coach` param reader and writer must use this hook:
 * nuqs shallow writes don't notify Next.js `useSearchParams`
 * consumers, so a raw reader would hold a stale value.
 */
export function useOpenState(): UseOpenStateReturn {
  const [coachParam, setCoachParam] = useQueryState(
    'coach',
    parseAsStringLiteral(COACH_PARAM_VALUES).withOptions({
      history: 'replace',
    })
  );

  const openCoach = useCallback(() => {
    void setCoachParam('on');
  }, [setCoachParam]);

  const closeCoach = useCallback(() => {
    void setCoachParam(null);
  }, [setCoachParam]);

  return { isOpen: coachParam === 'on', openCoach, closeCoach };
}
